import { useEffect, useState } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { GoogleMedium, FacebookMedium } from '@zoopla-eng/zds-components/icons';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

import { triggerTracking } from '@zoopla/tracking';
import { ErrorAlert } from '../ErrorAlert';
import {
    socialSignInCustomGridStyle,
    socialSignInErrorMessageStyle,
    socialSignInTextStyle,
    socialSignInTextWrapperStyle
} from './SocialAuth.styles.css';
import { useGoogle } from '../../hooks/useGoogle';
import { AuthMutationHookConsumerProps } from '../../hooks/authMutationHook.types';
import { useFacebook } from '../../hooks/useFacebook';

type SocialCopy = {
    cta?: string;
    facebook: string;
    google: string;
};

export type SocialAuthComponentProps = {
    type: 'signin' | 'register';
    variant?: 'horizontal' | 'vertical';
    copy: SocialCopy;
    isRememberMeChecked?: boolean;
    /**
     * Boolean to represent if we should reset error state
     */
    shouldReset?: boolean;
    /**
     * Function to reset any above error states if authentication is initiated by social buttons
     */
    resetErrors?: () => void;
    // TODO: Is this still needed?
    onClickTracking?: (clickType: 'facebook' | 'google') => void;
} & AuthMutationHookConsumerProps;

export const SocialAuth = ({
    trackingData,
    type,
    isRememberMeChecked,
    variant = 'horizontal',
    copy,
    onAuthentication,
    shouldReset,
    resetErrors,
    onClickTracking
}: SocialAuthComponentProps) => {
    const [loadingState, setLoadingState] = useState<
        'facebook' | 'google' | false
    >(false);
    const [error, setError] = useState('');

    const authenticateWithGoogle = useGoogle({
        trackingData
    });
    const authenticateWithFacebook = useFacebook({
        trackingData
    });

    const authenticate = async (type: 'facebook' | 'google') => {
        resetErrors?.();

        setLoadingState(type);
        setError('');

        const authenticationMutation =
            type === 'facebook'
                ? authenticateWithFacebook
                : authenticateWithGoogle;

        try {
            const user = await authenticationMutation(isRememberMeChecked);

            await onAuthentication(user);
        } catch (error) {
            if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Something went wrong');
            }

            // we only need to update the loading state if we have an error
            // in case everything works as intended the user will be moved to the next step
            setLoadingState(false);
        }
    };

    useEffect(() => {
        if (shouldReset) {
            setError('');
        }
    }, [shouldReset, setError]);

    return (
        <>
            <ErrorAlert>
                {error && (
                    <Box pb="S400">
                        <p
                            className={`${socialSignInErrorMessageStyle} ${fontGroups.fork}`}
                        >
                            {error}
                        </p>
                    </Box>
                )}
            </ErrorAlert>
            <div
                className={socialSignInCustomGridStyle({ direction: variant })}
            >
                <ButtonLeft
                    Icon={GoogleMedium}
                    category="secondary"
                    data-testid={`${type}-google`}
                    onClick={() => {
                        authenticate('google');

                        if (onClickTracking) {
                            onClickTracking('google');
                            return;
                        }

                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'google'
                        });
                    }}
                    loading={loadingState === 'google'}
                    loadingText="Loading"
                >
                    {copy.google}
                </ButtonLeft>
                <ButtonLeft
                    Icon={FacebookMedium}
                    category="secondary"
                    data-testid={`${type}-facebook`}
                    onClick={() => {
                        authenticate('facebook');

                        if (onClickTracking) {
                            onClickTracking('facebook');
                            return;
                        }

                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'facebook'
                        });
                    }}
                    loading={loadingState === 'facebook'}
                    loadingText="Loading"
                >
                    {copy.facebook}
                </ButtonLeft>
            </div>
            {copy.cta && (
                <Box pb="M300" pt="M200">
                    <div className={socialSignInTextWrapperStyle}>
                        <Box px="S200">
                            <p
                                className={`${socialSignInTextStyle} ${fontGroups.fork}`}
                            >
                                {copy.cta}
                            </p>
                        </Box>
                    </div>
                </Box>
            )}
        </>
    );
};
